import { useT } from '@kmgestao/i18n';
import { Badge, PageSectionCard, Skeleton } from '@kmgestao/ui';

import { formatDateTime, type AiKpisRecentError } from '../../../features/ai-analytics/index.js';

export interface RecentErrorsCardProps {
  errors: AiKpisRecentError[] | undefined;
  loading?: boolean;
}

export function RecentErrorsCard({ errors, loading = false }: RecentErrorsCardProps) {
  const { t } = useT('dashboard');
  const { t: tAiAdmin } = useT('ai-admin');

  return (
    <PageSectionCard
      title={t('errors.title')}
      description={t('errors.description')}
      contentClassName="space-y-4"
    >
      {loading
        ? Array.from({ length: 4 }).map((_, index) => (
            <Skeleton key={index} className="h-24 w-full" />
          ))
        : null}
      {!loading && (errors ?? []).length === 0 ? (
        <p className="text-base text-muted-foreground">{t('errors.empty')}</p>
      ) : null}
      {!loading ? (
        <ul className="space-y-3">
          {(errors ?? []).map((entry) => (
            <li key={entry.id} className="rounded-xl border border-border/70 bg-muted/20 p-4">
              <div className="flex flex-wrap items-center gap-2">
                <Badge variant={statusToVariant(entry.status)}>
                  {t(`errors.status.${entry.status}`, { defaultValue: entry.status })}
                </Badge>
                <span className="text-sm text-muted-foreground">
                  {formatDateTime(entry.createdAt)}
                </span>
                <span className="text-sm text-muted-foreground">•</span>
                <span className="text-sm text-muted-foreground">{entry.clinicName}</span>
              </div>
              <p className="mt-3 text-sm font-semibold sm:text-base">
                {tAiAdmin(`modules.keys.${entry.moduleKey}`, { defaultValue: entry.moduleKey })}
              </p>
              <p className="mt-1 text-sm leading-6 text-muted-foreground">
                {t('errors.meta', {
                  provider: entry.providerKey,
                  model: entry.modelId,
                })}
              </p>
              <p className="mt-3 break-words text-sm leading-6">{entry.errorMessage}</p>
            </li>
          ))}
        </ul>
      ) : null}
    </PageSectionCard>
  );
}

function statusToVariant(status: string): 'destructive' | 'secondary' | 'outline' {
  if (status === 'error' || status === 'blocked') return 'destructive';
  if (status === 'timeout' || status === 'rate_limited') return 'secondary';
  return 'outline';
}
